import React from "react";
import {
  ErrorCode,
  FileError,
  FileRejection,
  useDropzone,
} from "react-dropzone";
import "./ImageUpload.scss";
import isEmpty from "lodash/isEmpty";
import first from "lodash/first";

type ImageUploadProps = {
  buttonText: React.ReactNode;
  onChange?: (file: File) => void;
};

const ACCEPTED_IMAGE_TYPES: Record<string, string[]> = {
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
  "image/avif": [".avif"],
  "image/svg+xml": [".svg"],
};

const ImageUpload = ({ buttonText, onChange }: ImageUploadProps) => {
  const onDrop = (acceptedFiles: File[]) => {
    if (onChange && !isEmpty(acceptedFiles)) {
      onChange(acceptedFiles[0]);
    }
  };

  const validator = (file: File & { path?: string }): FileError | null => {
    const fileName = (file.path || file.name || "").toLowerCase();
    const extensionIndex = fileName.lastIndexOf(".");
    const extension = extensionIndex >= 0 ? fileName.slice(extensionIndex) : "";
    const allowedExtensions = new Set(
      Object.values(ACCEPTED_IMAGE_TYPES)
        .flat()
        .map((ext) => ext.toLowerCase()),
    );

    if (allowedExtensions.has(extension)) {
      return null;
    }

    return {
      message: "Invalid file type",
      code: ErrorCode.FileInvalidType,
    };
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: ACCEPTED_IMAGE_TYPES,
    onDrop,
    noDrag: true,
    multiple: false,
    maxSize: 1024 * 1024 * 2,
    validator,
    onError: (error) => {
      console.log("onError", error);
      alert("Something went wrong.");
    },
    onDropRejected: (files: FileRejection[]) => {
      const file = first(files);
      if (file) {
        if (file.errors.find((e) => e.code === "file-too-large")) {
          alert("File is too large. Max size is 2MB");
        }
        if (file.errors.find((e) => e.code === "file-invalid-type")) {
          alert(`Invalid file type.`);
        }
      } else {
        alert("Invalid file.");
      }
    },
  });

  return (
    <div className="image-upload">
      <div
        {...getRootProps({
          className: "dropzone",
          "aria-label": "Image Upload",
        })}
      >
        <input {...getInputProps()} />
        <div>{buttonText}</div>
      </div>
    </div>
  );
};

export default ImageUpload;
